import {join} from 'node:path';
import {isPlatformBrowser} from '@angular/common';
import {
  Component,
  DestroyRef,
  Inject,
  Injectable,
  PLATFORM_ID,
  Type,
  Pipe,
  ComponentRef,
} from '@angular/core';
import {Data} from '@angular/router';
import moment from 'moment';
import {HomepageForm} from '../../models/homepage-form';
import {
  MotorQuoteDeliveryFieldsEmailDTO,
  MotorQuoteDTO,
  MotorQuoteInputDTO,
  MotorQuoteInputOptionalsValueDTO,
  MotorQuoteWarrantyDTO,
} from '../../models/MotorQuoteDTO.interfaces';
import {InjectorService} from '../injector/injector.service';
import {environment} from './../../../environments/environment';
import {AnalyticsWarrantyData, DigitalData} from './../../../global.d';
import md5 from 'md5';
import {WarrantiesUuidEnum} from '../../enums/warranties-uuid.enum';
import {AnalyticsSubsection} from '../../enums/analytics-subsection';
import {DynamicComponentBaseComponent} from '../../../libs/dynamic-components/components/dynamic-component-base/dynamic-component-base.component';
import {DynamicComponent} from '../../../libs/dynamic-components/dynamic.component';
import {AzFormsComponent} from '../../../libs/az-forms/az-forms.component';
import {FormComponent} from '../../../libs/az-forms/components/form/form.component';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {catchError, firstValueFrom, Observable, of, Subject, take, tap} from 'rxjs';
import {GenericFieldDirective} from '../../../libs/az-forms/directives/generic-field/generic-field.directive';
import {TranslateService} from '@ngx-translate/core';
import {PageTypeService} from '../page-type/page-type.service';
import {digitalData} from '../../static/analyticsDigitalData';
import {DateHelperService} from '../date-helper/date-helper.service';

declare const _satellite: Record<string, unknown> & {
  track: (n: string, o: Record<string, unknown>) => void;
};

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private isBrowser: boolean;
  private moduleListHash: string = '';
  private TYPtype: null | 'payment' | 'save' = null;
  private isFunnelOverridden = false;
  private funnel: 'fastquote' | 'allianz_pay' = 'fastquote';
  private isLithium: boolean = false;
  private customMotorLabels: {
    lithiumId: string[];
    labels: Record<string, string | Record<string, string>>;
  } = {lithiumId: [], labels: {}};
  private isEcommerceActiveForAgency!: boolean;

  staticDigitalData!: Partial<DigitalData>;
  _deviceType!: 'tablet' | 'desktop' | 'smartphone';

  constructor(
    private injectorService: InjectorService,
    private translateService: TranslateService,
    private pageTypeService: PageTypeService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private destroyRef: DestroyRef,
    private dateHelperService: DateHelperService,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    const t = this.translateService.get('customMotor').subscribe((labels) => {
      this.customMotorLabels = {labels, lithiumId: Object.keys(labels?.lithium ?? {})};
    });
  }

  track(obj: Record<string, unknown>, evt = 'fastquote') {
    if (isPlatformBrowser(this.platformId)) {
      if (!environment.production) {
        console.log('_satellite.track', obj);
      }
      _satellite.track(evt, obj);
    }
  }

  textForAnalytics(text: string) {
    return text?.toLowerCase().trim().replace(/ - /g, '-').replace(/ /g, '_');
  }

  private _getDeviceType(): 'tablet' | 'desktop' | 'smartphone' {
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(navigator.userAgent)) {
      return 'tablet';
    } else if (
      /Mobile|iP(hone|od|ad)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(
        navigator.userAgent,
      )
    ) {
      return 'smartphone';
    } else {
      return 'desktop';
    }
  }

  dataToUpdate(data: Partial<DigitalData>) {
    if (this.isBrowser) {
      window.digitalData = {...window.digitalData, ...data};
    }
  }

  getDeviceType() {
    if (this._deviceType) {
      return this._deviceType;
    }
    this._deviceType = this._getDeviceType();
    return this._deviceType;
  }

  getScript(): string {
    const position = this.isProd() ? 'prd' : 'stg';
    return environment.analytics[position];
  }

  isProd() {
    if (typeof window !== 'undefined') {
      return window.location.href.indexOf('allianz.it') >= 0;
    }
    return false;
  }

  private getEnvironment() {
    const envs = {
      dv: 'development',
      te: 'test',
      pp: 'preproduction',
    };
    const hostName = window.location.pathname;
    if (hostName === 'localhost') {
      return 'local';
    }
    const res = hostName.match(/^[a-z]+\.(?<environment>[a-z]{2,3})\.azi/);
    if (res?.groups?.['environment']) {
      const env: 'dv' | 'te' | 'pp' = res.groups['environment'] as 'dv' | 'te' | 'pp';
      return envs[env] ?? 'no-prod';
    }

    return this.isProd() ? 'production' : 'no-prod';
  }

  anonymize(string: string) {
    return md5(string);
  }

  private setBaseDigitalData() {
    if (!this.staticDigitalData) {
      this.staticDigitalData = {
        env_channel: this.getDeviceType(),
        enviroment: this.getEnvironment(),
      };
      this.dataToUpdate(this.staticDigitalData);
    }
  }

  public injectAdobeLaunchScript() {
    try {
      const script = this.getScript();
      // _satellite.pageBottom();
      if (this.injectorService.hasScript(script)) {
        console.info('adobe-script already injected');
        return of(true);
      } else {
        if (this.isBrowser) {
          window.digitalData = digitalData;
          this.setBaseDigitalData();
        }
        return this.injectorService.add(script, 'launch', true).pipe(
          catchError((err) => {
            console.warn('Adobe Script not loaded', err);
            return of(false);
          }),
          tap((_res) => console.info('adobe-script injected')),
          take(1),
        );
      }
    } catch (error) {
      console.warn('analytics error', error);
      return of(false);
    }
  }

  setOverrideFunnelType(funnel: 'fastquote' | 'allianz_pay', reset?: boolean) {
    this.isFunnelOverridden = reset ? false : true;
    this.funnel = funnel;
  }

  updateDigitalData(data: Data) {
    if (this.isBrowser) {
      if (data['analyticsFunnel'] && !this.isFunnelOverridden) {
        this.funnel = data['analyticsFunnel'];
      }
      const dataToUpdate: Partial<DigitalData> = {
        pagename: data['pageName'],
        url: window.location.href,
        section:
          data['type'] === 'allianzPay' || this.isFunnelOverridden ? 'allianzpay' : 'fastquote',
        sub_section: data['subSection'] ?? null,
        step_checkout: data['AnalyticsStep'] ?? null,
        timestamp_visits: moment().format('HH:mm DD/MM/YYYY'),
      };
      this.TYPtype =
        data?.['subSection'] === AnalyticsSubsection.THANKYOU_PAGE ||
        data?.['subSection'] === AnalyticsSubsection.PURCHASE_ALLIANZ_PAY
          ? data?.['isPaymentOk']
            ? 'payment'
            : 'save'
          : null;
      this.setBaseDigitalData();
      this.dataToUpdate(dataToUpdate);
    }
  }

  sendAnalytics(
    obj: (Record<string, unknown> & {funnel_type?: never}) | string,
    funnelOverride?: 'fastquote' | 'allianz_pay',
    evt?: string,
  ) {
    if (typeof obj === 'object') {
      const trackObj: {[key: string]: string; funnel_type: 'fastquote' | 'allianz_pay'} = {
        ...obj,
        funnel_type: funnelOverride ?? this.funnel,
      };
      this.track(trackObj, evt);
    } else {
      console.log('Emissione Analytics da ', obj);
    }
  }

  startQuotation(data: HomepageForm, triggerEvent = true) {
    const dataToUpdate: Partial<DigitalData> = {
      user_Id: this.anonymize(data.insuredEmail),
      data_nascita: data.insuredBirthday,
      targa: this.anonymize(data.vehiclePlate?.toUpperCase()),
    };
    this.dataToUpdate(dataToUpdate);
    if (triggerEvent)
      this.sendAnalytics({
        componente: 'quotatore',
        cta: 'fai_preventivo',
        data_nascita: dataToUpdate.data_nascita ?? '',
        targa: dataToUpdate.targa ?? '',
        hit_type: 'event',
      });
  }

  startRecoverConfiguration(motorQuote: MotorQuoteDTO) {
    const email = motorQuote.response.insuranceData.contractor.fieldset[0].fields.email;
    const birthdate = motorQuote.response.insuranceData.contractor.fieldset[0].fields.birthdate;
    const plate = motorQuote.response.insuranceData.vehicle.fieldset[0].fields.plate;
    const dataToUpdate: Partial<DigitalData> = {
      user_Id: this.anonymize(email.displayValue ?? email.value ?? ''),
      data_nascita: birthdate.displayValue ?? birthdate.value ?? null,
      targa: this.anonymize((plate.displayValue ?? plate.value ?? '')?.toUpperCase()),
    };
    this.dataToUpdate(dataToUpdate);
  }

  getWarranty(motorQuote: MotorQuoteDTO, uuid: string) {
    return motorQuote.response.configuration.warranties.find((el) => el.uuid === uuid);
  }
  getFieldAsObject(warranty: MotorQuoteWarrantyDTO) {
    return warranty.fields?.reduce(
      (obj: Record<string, MotorQuoteInputDTO>, el: MotorQuoteInputDTO) => {
        obj[el.uuid] = el;
        return obj;
      },
      {},
    );
  }
  getPlan(motorQuote: MotorQuoteDTO) {
    return motorQuote.response.plans.find((el) => el.active === true);
  }
  getWarrantyName(warranty: MotorQuoteWarrantyDTO): string {
    const baseObj =
      this.isLithium && this.customMotorLabels.lithiumId.indexOf(warranty.uuid) >= 0
        ? this.customMotorLabels.labels['lithium']
        : this.customMotorLabels.labels;
    const name = (<Record<string, string>>baseObj)[warranty.uuid];
    return name ?? warranty.name;
  }
  setModulo(warranty: MotorQuoteWarrantyDTO): AnalyticsWarrantyData {
    const name = this.getWarrantyName(warranty);
    return {
      id_modulo: warranty.uuid,
      elemento: this.textForAnalytics(name),
      prezzo_modulo: warranty.price.toString(),
      evidenza_modulo: warranty.recommended ? 'vero' : 'falso',
    };
  }

  private checkAndAddModules(motorQuote: MotorQuoteDTO): null | AnalyticsWarrantyData[] {
    const warranties = motorQuote.response.configuration.warranties;
    const md5 = this.anonymize(JSON.stringify(warranties));
    if (this.moduleListHash === md5) {
      return null;
    }
    this.moduleListHash = md5;
    return warranties.reduce((arr: AnalyticsWarrantyData[], el: MotorQuoteWarrantyDTO) => {
      if (el.selected) {
        arr.push(this.setModulo(el));
      }

      return arr;
    }, []);
  }

  private getOptionalsData(list: MotorQuoteInputOptionalsValueDTO[]): Record<'1' | '2', string[]> {
    if (!list) {
      return {
        '1': [],
        '2': [],
      };
    }
    return list.reduce(
      (obj: Record<'1' | '2', string[]>, el) => {
        obj[<'1' | '2'>el.group].push(this.textForAnalytics(el.value));
        return obj;
      },
      {'1': [], '2': []},
    );
  }

  setEcommerceActiveForAgency(bool: boolean) {
    this.isEcommerceActiveForAgency = bool;
  }

  updateQuote(motorQuote: MotorQuoteDTO) {
    if (this.isBrowser && motorQuote.response) {
      this.isLithium = this.pageTypeService.getTypePage() === 'energia';
      const rca = this.getWarranty(motorQuote, WarrantiesUuidEnum.RCA);
      const rcaFields = rca && this.getFieldAsObject(rca);
      const plan = this.getPlan(motorQuote);
      const isClient = motorQuote.response.configuration.conditions.clienteAllianz;
      const insuranceData = motorQuote.response.insuranceData.contractor.fieldset?.[0].fields;
      const vehicle = motorQuote.response.insuranceData.vehicle.fieldset[0].fields;
      const optionals = this.getOptionalsData(vehicle.optionals.value ?? []);
      const additionalData: Partial<DigitalData> = {};
      additionalData.agenzia_se_gia_cliente = isClient
        ? motorQuote.response.configuration.agency.cod
        : '';
      
      const estimatedValue = (
        vehicle?.estimatedValue?.displayValue ??
        vehicle?.estimatedValue?.value ??
        '0'
      )
        .replaceAll('.', '')
        .replace(',', '.');

      const data: Partial<DigitalData> = {
        tipo_cliente: isClient ? 'cliente_allianz' : 'nuovo_cliente',
        codice_agenzia: motorQuote.response.configuration.agency.cod ?? null,
        timestamp_visits: moment().format('HH:mm DD/MM/YYYY'),
        provincia: insuranceData?.province.displayValue ?? null,
        cap: insuranceData?.zipCode.value ?? insuranceData?.zipCode.displayValue ?? null,
        ...this.dateHelperService.checkDateForAnalytics(motorQuote).analyticsDateObj,
        modalita_invio:
          (<MotorQuoteDeliveryFieldsEmailDTO>(
            motorQuote.response.insuranceData.delivery.fieldset[0].fields
          )).mode.value === '1'
            ? 'virtuale'
            : 'cartaceo',
        tipo:
          (
            motorQuote.response.insuranceData.vehicle.fieldset[0].fields.type.displayValue ??
            motorQuote.response.insuranceData.vehicle.fieldset[0].fields.type.value
          )?.toLowerCase() ?? null,
        prezzo_base: rca?.price ?? 0,
        prezzo_totale: motorQuote.response.configuration.currentPrice.total,
        tasse: motorQuote.response.configuration.currentPrice.tax ?? 0,
        sconto: plan?.discount,
        sconto_convenzione: plan?.discountCanale ?? 0,
        modello_versione: `${vehicle.manufacturer.displayValue} ${vehicle.model.displayValue} ${vehicle.variant.displayValue}`,
        massimale: rcaFields?.[WarrantiesUuidEnum.MASSIMALE].displayValue ?? null,
        proteggi_classe_merito:
          rcaFields?.[WarrantiesUuidEnum.PROTEZIONE_BONUS]?.value === '1' ? 'vero' : 'falso',
        tipo_guida:
          rcaFields?.[WarrantiesUuidEnum.CONDUCENTE_TIPO_GUIDA].displayValue?.toLowerCase(),
        protezione_rivalsa:
          rcaFields?.[WarrantiesUuidEnum.PROTEZIONE_RIVALSA].value === '1' ? 'vero' : 'falso',
        rimorchio: vehicle.trailerCoupling.value ? 'vero' : 'falso',
        antifurto: this.textForAnalytics(vehicle.immobilizer.displayValue ?? ''),
        tipo_pagamento:
          motorQuote.response.configuration.payment.widget.options
            ?.find((el) => el.value === motorQuote.response.configuration.payment.value)
            ?.label.toLowerCase()
            .replace(/ /g, '_') ?? null,
        convenzione: motorQuote.response.configuration.agreement?.value ? 'si' : 'no',
        metodo_di_pagamento: motorQuote.response?.paymentData?.channel ?? null,

        tipo_privacy:
          motorQuote.response.insuranceData.authorization.fieldset[0].fields.specialOffer.value ===
          'true'
            ? 'si'
            : 'no',
        numero_polizza: motorQuote.response.ticket ?? null,
        id_transazione: motorQuote.response.paymentData?.order ?? null,
        id_ordine_finale_salvataggio: motorQuote.response.ticket, //this.TYPtype === 'save' ? motorQuote.response.ticket : null,
        fq_tipo_funnel: motorQuote.response.configuration.conditions.hasDiscountBlueSky
          ? 'voucher_25euro'
          : 'standard',
        tipo_motore: this.textForAnalytics(vehicle?.fuelType?.displayValue ?? '') ?? null,
        valore_veicolo: parseFloat(estimatedValue).toFixed(2),
        custodia_box: vehicle?.box?.displayValue ?? '',
        iva_agevolata: vehicle?.reducedVat?.value === 'true' ? 'vero' : 'falso',
        valore_accessori_optional: optionals['1'].length > 0 ? optionals['1'].join('-') : 'no',
        pacchetti_optional: optionals['2'].length > 0 ? optionals['2'].join('-') : 'no',
        agenzia_permette_ecommerce: motorQuote.response.configuration.agency.cod? (this.isEcommerceActiveForAgency ? 'permette' : 'non_permette') : null,
      };

      const modules = this.checkAndAddModules(motorQuote);
      if (modules && modules.length > 0) {
        data.Modulo = modules;
      }

      const finalData = {...data, ...(isClient ? additionalData : {})};
      this.dataToUpdate(finalData);
    }
  }

  cleanDigitalDataFromQuotation() {
    const cleanDigitalData: Partial<DigitalData> = {
      user_Id: null,
      tipo_cliente: null,
      agenzia_se_gia_cliente: null,
      agenzia_permette_ecommerce: null,
      codice_agenzia: null,
      timestamp_visits: null,
      provincia: null,
      cap: null,
      flag_allianz_hp: null,
      data_nascita: null,
      targa: null,
      scadenza: null,
      giorni_scadenza: null,
      tipo_scadenza: null,
      modalita_invio: null,
      tipo: null,
      prezzo_base: null,
      prezzo_totale: null,
      tasse: null,
      sconto: null,
      sconto_convenzione: null,
      modello_versione: null,
      massimale: null,
      proteggi_classe_merito: null,
      tipo_guida: null,
      protezione_rivalsa: null,
      rimorchio: null,
      antifurto: null,
      tipo_pagamento: null,
      convenzione: null,
      metodo_di_pagamento: null,
      tipo_privacy: null,
      numero_polizza: null,
      id_transazione: null,
      id_ordine_finale_salvataggio: null,
      Modulo: [],
      fq_tipo_funnel: null,
      tipo_motore: null,
      valore_veicolo: null,
      custodia_box: null,
      iva_agevolata: null,
      valore_accessori_optional: null,
      pacchetti_optional: null,
    };
    this.dataToUpdate(cleanDigitalData);
  }
}
